/**
 * Che cos'è una notte, quando la si guarda per decidere.
 *
 * Una notte del calendario ha due informazioni separate: se è **chiusa**
 * (`status`) e se dietro c'è una **prenotazione** (`booked`). Il gestionale le
 * tiene distinte perché sono cose diverse, e chi le confonde sbaglia sempre
 * nello stesso modo: `!booked` letto come «libera».
 *
 * Non è vero. Una notte chiusa senza prenotazione è un **blocco**: l'host ci
 * sta lui, ci va un lavoro, la casa è dal muratore, o semplicemente quelle
 * date non le vende. È una decisione presa, non un buco da riempire — e
 * trattarla come un buco significa contarla fra le notti invendute, proporci
 * sopra uno sconto, e far scendere un'occupazione che è giusta così.
 *
 * Da qui in poi le notti si guardano con queste tre lenti, mai con `!booked`.
 */

import type { DayAvailability } from "./actions.js";

/** Il minimo che serve per giudicare una notte: com'è chiusa, e da cosa. */
export interface NotteDiCalendario {
  status: DayAvailability;
  booked: boolean;
}

/**
 * Chiusa a mano: nessuno ci dorme pagando, e nessuno può prenotarla.
 *
 * Non entra fra le notti libere (non è in vendita) e non entra fra le vendute
 * (non porta un euro): sta fuori da entrambi i conti, e va detto quante sono.
 */
export function notteBloccata(notte: NotteDiCalendario): boolean {
  return !notte.booked && notte.status !== "available";
}

/**
 * In vendita e ancora da vendere: è questa la notte su cui ha senso
 * abbassare un prezzo o aprire un minimo.
 */
export function notteLibera(notte: NotteDiCalendario): boolean {
  return !notte.booked && notte.status === "available";
}

/**
 * Sul mercato: venduta o ancora in vendita.
 *
 * È il denominatore giusto di un'occupazione. Contare le notti bloccate
 * significa dire «sei pieno al 40%» a chi ha venduto tutto quello che aveva
 * messo in vendita.
 */
export function notteInVendita(notte: NotteDiCalendario): boolean {
  return !notteBloccata(notte);
}

/** Quante sono, in un colpo solo: vendute, libere, bloccate. */
export function contaNotti<T extends NotteDiCalendario>(
  notti: readonly T[],
): { vendute: number; libere: number; bloccate: number; inVendita: number } {
  let vendute = 0;
  let libere = 0;
  let bloccate = 0;

  for (const notte of notti) {
    if (notte.booked) vendute += 1;
    else if (notteBloccata(notte)) bloccate += 1;
    else libere += 1;
  }

  return { vendute, libere, bloccate, inVendita: vendute + libere };
}
